import { useMutation, useQueryClient } from '@tanstack/react-query';
import { commonClient } from 'api';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { EnvironmentConfiguration } from './environmentConfiguration';
import {
  ENVIRONMENT_CONFIGURATION_KEY,
  parseEnvironmentConfiguration
} from './environmentConfiguration';
import type { ConfigurationItem } from './models';
import { getConfigurationKeyBuilderV2 } from './queries';

/** Serialize writes and coalesce pending changes so the latest complete preference wins. */
export const useEnvironmentConfiguration = (initialConfiguration: ConfigurationItem[]) => {
  const [configuration, setConfiguration] = useState(() =>
    parseEnvironmentConfiguration(initialConfiguration)
  );
  const latest = useRef(configuration);
  const pending = useRef<EnvironmentConfiguration>();
  const saving = useRef(false);
  const active = useRef(true);
  const [saveFailed, setSaveFailed] = useState(false);
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: (value: EnvironmentConfiguration) =>
      commonClient.editConfigurationV2({
        key: ENVIRONMENT_CONFIGURATION_KEY,
        isCompanyDefault: false,
        value: JSON.stringify(value)
      }),
    retry: false
  });

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const flush = useCallback(async () => {
    if (saving.current) return;
    saving.current = true;
    setSaveFailed(false);
    try {
      while (pending.current && active.current) {
        const next = pending.current;
        pending.current = undefined;
        try {
          await mutateAsync(next);
          if (!active.current) return;
          queryClient.setQueryData<ConfigurationItem[]>(getConfigurationKeyBuilderV2(), (old) => [
            ...(old ?? []).filter((item) => item.key !== ENVIRONMENT_CONFIGURATION_KEY),
            {
              key: ENVIRONMENT_CONFIGURATION_KEY,
              value: JSON.stringify(next),
              isUserSpecific: true
            }
          ]);
        } catch {
          if (!active.current) return;
          // A newer selection can still succeed. Otherwise retain the failed selection for retry.
          if (!pending.current) {
            setSaveFailed(true);
            break;
          }
        }
      }
    } finally {
      saving.current = false;
    }
  }, [mutateAsync, queryClient]);

  const updateNavigation = useCallback(
    (patch: Partial<EnvironmentConfiguration['navigation']>) => {
      const next = { ...latest.current, navigation: { ...latest.current.navigation, ...patch } };
      latest.current = next;
      setConfiguration(next);
      pending.current = next;
      void flush();
    },
    [flush]
  );

  const retrySave = useCallback(() => {
    pending.current = latest.current;
    void flush();
  }, [flush]);

  return { configuration, updateNavigation, saveFailed, retrySave };
};
